import { useCallback, useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent, type PointerEvent } from 'react';
import type { AnchorRect } from './GameTooltip';

/** How long a mouse has to rest on a link before its tooltip opens (and anything is fetched). */
export const HOVER_DELAY_MS = 150;

/**
 * Open/close state for a hover tooltip: opens after the hover delay for a mouse or pen, at once
 * on keyboard focus, never for touch (a tap just follows the link, and the focus it gives the
 * link is ignored). Escape, leaving, blurring, scrolling and resizing close it, since the
 * anchor's position is then stale.
 */
export function useHoverTooltip() {
  const id = useId();
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A tap focuses the link it lands on; that focus must not open the tooltip either.
  const touchPress = useRef(false);

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const close = useCallback(() => {
    clear();
    setAnchor(null);
  }, [clear]);
  const openAt = useCallback((el: Element) => {
    const r = el.getBoundingClientRect();
    setAnchor({ left: r.left, top: r.top, right: r.right, bottom: r.bottom });
  }, []);

  useEffect(() => clear, [clear]);
  useEffect(() => {
    if (!anchor) return;
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [anchor, close]);

  const handlers = {
    onPointerEnter: (e: PointerEvent<HTMLElement>) => {
      if (e.pointerType === 'touch') return;
      const el = e.currentTarget;
      clear();
      timer.current = setTimeout(() => openAt(el), HOVER_DELAY_MS);
    },
    onPointerLeave: () => close(),
    onPointerDown: (e: PointerEvent<HTMLElement>) => {
      touchPress.current = e.pointerType === 'touch';
    },
    onFocus: (e: FocusEvent<HTMLElement>) => {
      if (touchPress.current) {
        touchPress.current = false;
        return;
      }
      clear();
      openAt(e.currentTarget);
    },
    onBlur: () => close(),
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Escape' && anchor) close();
    },
  };

  return { id, open: anchor !== null, anchor, handlers };
}
